<template>
    <div class="teamschedule-list">
        <div class="facet-header">
            <span class="facet-name">{{ facetName }}</span>
            <div
                v-if="shouldShowMatchMode"
                class="match-mode-wrapper"
            >
                <button
                    ref="matchModeButton"
                    type="button"
                    class="match-mode-button"
                    :aria-label="`Match mode: ${matchMode === 'any' ? 'Any' : 'All'}`"
                    :aria-expanded="showMatchModeDropdown"
                    aria-haspopup="listbox"
                    @click="toggleMatchModeDropdown"
                    @keydown.escape="closeMatchModeDropdown"
                    @keydown.arrow-down.prevent="focusFirstOption"
                >
                    {{ matchMode === 'any' ? 'Any' : 'All' }}
                    <i
                        class="playvox-icons match-mode-arrow"
                        :class="showMatchModeDropdown ? 'icon-chevron-up' : 'icon-chevron-down'"
                        aria-hidden="true"
                    />
                </button>
                <div
                    v-if="showMatchModeDropdown"
                    ref="matchModeDropdown"
                    class="match-mode-dropdown"
                    role="listbox"
                    :aria-label="`Select match mode`"
                    @keydown.escape="closeMatchModeDropdown"
                    @keydown.arrow-up.prevent="focusPreviousOption"
                    @keydown.arrow-down.prevent="focusNextOption"
                >
                    <button
                        ref="matchAllOption"
                        type="button"
                        role="option"
                        class="match-mode-option"
                        :aria-selected="matchMode === 'all'"
                        @click="selectMatchMode('all')"
                        @keydown.enter="selectMatchMode('all')"
                        @keydown.space.prevent="selectMatchMode('all')"
                        @keydown.tab="closeMatchModeDropdownOnTab"
                        @keydown.escape="closeMatchModeDropdown"
                    >
                        <span class="option-text">Match All</span>
                        <img
                            v-if="matchMode === 'all'"
                            :src="CheckIcon"
                            alt=""
                            class="checkmark"
                        >
                    </button>
                    <button
                        ref="matchAnyOption"
                        type="button"
                        role="option"
                        class="match-mode-option"
                        :aria-selected="matchMode === 'any'"
                        @click="selectMatchMode('any')"
                        @keydown.enter="selectMatchMode('any')"
                        @keydown.space.prevent="selectMatchMode('any')"
                        @keydown.tab="closeMatchModeDropdownOnTab"
                        @keydown.escape="closeMatchModeDropdown"
                    >
                        <span class="option-text">Match Any</span>
                        <img
                            v-if="matchMode === 'any'"
                            :src="CheckIcon"
                            alt=""
                            class="checkmark"
                        >
                    </button>
                </div>
            </div>
        </div>
        <div
            class="multi-select-wrapper"
            @focusout="resetSearch"
        >
            <al-multi-select
                ref="multiSelectRef"
                :value="selected"
                size="large"
                style="--size-control-default:36px; width: 100%;"
                class="multi-select"
                placeholder="Select teams"
                variant="wfm"
                test-id="workstreamSelect"
                @change="emitSelected($event,$event)"
                :key="paginatedOptions.length"
            >
                <!-- eslint-disable -->
            <div v-if="selected.length === 0" slot="label" test-id="workstreamSelect" class="facet-label">None selected</div>
            <!-- eslint-enable -->
                <al-select-option
                    v-for="option in paginatedOptions"
                    :key="option.id"
                    :value="option.id"
                    :display-value="option.label"
                >
                    {{ option.label }}
                </al-select-option>
            </al-multi-select>
        </div>
    </div>
</template>

<script setup lang="ts">
/* eslint-disable max-lines */
import {
    ref, computed, inject, onMounted, watch
} from 'vue';
import { useSelectedViewFacet } from '@/components/TeamAndSchedule/composables/useSelectedViewFacet';
import { useMatchMode } from '@/components/TeamAndSchedule/composables/useMatchMode';
import { FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import { loadFacetData } from '@/components/TeamAndSchedule/composables/useFacetSessionLoader';
import '@playvox/almendra/dist/components/icon';
import '@playvox/almendra/dist/components/checkbox';
import '@playvox/almendra/dist/components/input';
import CheckIcon from '@/assets/images/check.svg?url';

const pagePreferences = inject<InjectedPagePreferences>('pagePreferences');
const resetFilters = inject('resetFilters', ref(false));
const multiSelectRef = ref<{ numberOfVisibleOptions?: number; numberOfOptions?: number } | null>(null);

interface TeamScheduleOption {
    id: number;
    label: string;
    count: number;
}

const props = defineProps<{
    options: TeamScheduleOption[];
    facetId: string;
    facetName: string;
    urlKey?: string;
    isLoading: boolean;
    activeTable: string;
    labelMap?: Record<string, string>;
    }>();

const emit = defineEmits(['change']);
let selected = ref<string>('');

const emitChangeWithMatchMode = (data?: { matchMode?: string }) => {
    emit('change', {
        value: selected.value,
        facetId: props.facetId,
        facetName: props.facetName,
        urlKey: props.urlKey,
        matchMode: data?.matchMode || matchMode.value,
        shouldUpdate: isLastUnchecked()
    });
};

const {
    matchMode,
    showMatchModeDropdown,
    shouldShowMatchMode,
    toggleMatchModeDropdown,
    selectMatchMode
} = useMatchMode(props.facetId, emitChangeWithMatchMode);

// Refs for keyboard navigation
const matchModeButton = ref<HTMLButtonElement | null>(null);
const matchModeDropdown = ref<HTMLDivElement | null>(null);
const matchAllOption = ref<HTMLButtonElement | null>(null);
const matchAnyOption = ref<HTMLButtonElement | null>(null);
const currentFocusIndex = ref(0);

// Keyboard navigation functions
const closeMatchModeDropdown = () => {
    if (showMatchModeDropdown.value) {
        toggleMatchModeDropdown();
        matchModeButton.value?.focus();
    }
};

const closeMatchModeDropdownOnTab = () => {
    // Close dropdown but don't return focus (let Tab naturally move focus)
    if (showMatchModeDropdown.value) {
        toggleMatchModeDropdown();
    }
};

const focusFirstOption = () => {
    if (showMatchModeDropdown.value) {
        currentFocusIndex.value = 0;
        matchAllOption.value?.focus();
    }
};

const focusNextOption = () => {
    if (currentFocusIndex.value === 0) {
        currentFocusIndex.value = 1;
        matchAnyOption.value?.focus();
    }
};

const focusPreviousOption = () => {
    if (currentFocusIndex.value === 1) {
        currentFocusIndex.value = 0;
        matchAllOption.value?.focus();
    }
};

// Focus first option when dropdown opens
watch(showMatchModeDropdown, (isOpen) => {
    if (isOpen) {
        import('vue').then(({ nextTick }) => {
            nextTick(() => {
                if (matchMode.value === 'all') {
                    currentFocusIndex.value = 0;
                    matchAllOption.value?.focus();
                } else {
                    currentFocusIndex.value = 1;
                    matchAnyOption.value?.focus();
                }
            });
        });
    }
});

const applyFacetData = (facetData: { value?: string; matchMode?: string }): void => {
    selected.value = facetData?.value || '';
    matchMode.value = (facetData?.matchMode as 'any' | 'all') || 'any';
};

const loadSelectedValuesFromUrl = () => {
    if (!props.options?.length) return;

    const facetData = loadFacetData<string>(props.activeTable, props.facetId);
    applyFacetData(facetData || {});
};

const applyCustomStyles = () => {
    const multiSelects = document.querySelectorAll('al-multi-select.multi-select');
    multiSelects.forEach((multiSelect) => {
        if (multiSelect && multiSelect.shadowRoot) {
            let styleTag = multiSelect.shadowRoot.querySelector('#custom-styles');
            if (!styleTag) {
                styleTag = document.createElement('style');
                styleTag.id = 'custom-styles';
                styleTag.textContent = `
                    #multi-select-focus {
                        background-color: white !important;
                        border-radius: 4px !important;
                        border: 1px solid #DCE3E4 !important;
                        max-width: 164px !important;
                        height: 28px !important;
                    }
                `;
                multiSelect.shadowRoot.appendChild(styleTag);
            }
        }
    });
};

onMounted(() => {
    applyCustomStyles();
});

const areOptionsEqual = (newOpts: TeamScheduleOption[], oldOpts?: TeamScheduleOption[]): boolean => {
    if (!oldOpts || newOpts.length !== oldOpts.length) return false;
    return newOpts.every((newOpt, index) => newOpt.id === oldOpts[index].id);
};

watch(() => props.options, (newOptions, oldOptions) => {
    if (newOptions && newOptions.length > 0 && !areOptionsEqual(newOptions, oldOptions)) {
        loadSelectedValuesFromUrl();
    }
}, { immediate: true });

watch(() => props.options, () => {
    // Use nextTick to ensure DOM is updated
    import('vue').then(({ nextTick }) => {
        nextTick(() => applyCustomStyles());
    });
});

function resetSearch() {
    // Almendra internal API – exists in v2.59.0
    if (multiSelectRef.value?.numberOfVisibleOptions || multiSelectRef.value?.numberOfVisibleOptions === 0) {
        multiSelectRef.value.numberOfVisibleOptions = multiSelectRef.value?.numberOfOptions;   // clears search + filtered items
    }
}
const searchQuery = ref('');
const isLastUnchecked = () => {
    return selected.value.length === 0;
};

const emitSelected = (event: Event & { detail?: { value?: TeamScheduleOption[] } }, _eventNew: unknown) => {
    const target = event.target as HTMLInputElement | null;
    const eventValue = event.detail?.value || target?.value;
    if (typeof eventValue === 'string') {
        selected.value = eventValue.split(',').map((id) => id.trim()).filter((id) => id !== '').join(',');
    } else {
        const values = (eventValue || []) as TeamScheduleOption[];
        selected.value = values.map((v) => String(v.id)).join(',');
    }
    emitChangeWithMatchMode();
};

const filteredOptions = computed(() => {
    if (!searchQuery.value) {
        return props.options;
    }
    return props.options.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
});

const paginatedOptions = computed(() => filteredOptions.value || []);

const onSelectedViewFacetChange = (value: unknown) => {
    const selectedViewValue = value as FacetItem;
    if (sessionStorage.getItem('usersData')) {
        if (typeof selectedViewValue?.value === 'string') {
            selected.value = selectedViewValue.value;
        } else {
            selected.value = '';
        }
    }
};

useSelectedViewFacet({
    pagePreferences,
    activeTable: props.activeTable,
    facetId: props.facetId,
    resetFilters,
    onSelectedViewFacetChange
});
</script>

<style scoped>
.header {
    cursor: pointer;
    display: flex;
    width: 200px;
    padding: var(--spacing-1, 4px) 0px;
    align-items: center;
    gap: var(--spacing-2, 8px);
    line-height: var(--spacing-6, 24px);
}
.facet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    gap: 4px;
    padding-right: 0;
    min-height: 20px;
    max-height: 20px;
    line-height: 20px;
    max-width: 155px;
}
.facet-name {
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #1A2021;
    white-space: nowrap;
    flex-shrink: 0;
}
.match-mode-wrapper {
    position: relative;
    display: inline-block;
    flex-shrink: 0;
}
.match-mode-button {
    display: flex;
    align-items: center;
    gap: 0px;
    padding: 4px;
    border: none;
    background: transparent;
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    color: #31708F;
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
    border-radius: 4px;
}
.match-mode-button:hover {
    color: #2a5f7a;
    background-color: rgba(49, 112, 143, 0.05);
}
.match-mode-button:focus-visible {
    outline: 2px solid #101010;
    outline-offset: 1px;
    position: relative;
    z-index: 1;
}
.match-mode-arrow {
    font-family: playvox-icons;
    font-weight: 400;
    font-size: 24px;
    line-height: 24px;
    width: 24px;
    height: 24px;
    letter-spacing: 0;
    color: #31708F !important;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.match-mode-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 2px;
    background: #fff;
    border: 1px solid #DCE3E4;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    z-index: 1000;
    min-width: 140px;
}
.match-mode-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    white-space: nowrap;
    width: 100%;
    border: none;
    background: none;
    text-align: left;
    transition: background-color 0.15s;
}
.match-mode-option:first-child {
    border-radius: 4px 4px 0 0;
}
.match-mode-option:last-child {
    border-radius: 0 0 4px 4px;
}
.match-mode-option:hover {
    background-color: #f5f5f5;
}
.match-mode-option:focus-visible {
    outline: none;
    background-color: #f5f5f5;
}
.option-text {
    flex: 1;
}
.checkmark {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    margin-left: 8px;
}
.teamschedule-list {
    width: 100%;
    font-family: Roboto;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    color: #1A2021;
}

.multi-select-wrapper {
    margin-top: 4px;
}

:deep(.multi-select) {
    padding-top: 0;
    font-family: Roboto !important;
}

:deep(.multi-select)::part(control) {
    border: 1px solid #DCE3E4 !important;
    font-family: Roboto !important;
    height: 28px !important;
}

:deep(.multi-select)::part(combobox) {
    font-family: Roboto !important;
}

:deep(.multi-select)::part(input) {
    font-family: Roboto !important;
}

.facet-label {
    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    line-height: 20px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: #1A2021;
}

.multi-select::part(label) {
    background: transparent !important;
}
.multi-select [slot="label"] {
    background: transparent !important;
}

.teamschedule-list label {
    display: flex;
    padding: 0px var(--spacing-2, 8px);
    align-items: center;
    gap: var(--spacing-2, 8px);
}
.teamschedule-list-item {
    display: flex;
    padding: var(--spacing-1, 4px);
    align-items: center;
    gap: var(--spacing-2, 8px);
}
.teamschedule-list-search {
    margin-bottom: var(--spacing-2, 8px);
    padding: 0px var(--spacing-2, 8px);
    width: 100%;
    box-sizing: border-box;
}
.show-more {
    display: flex;
    padding: var(--spacing-1, 4px);
    align-items: center;
    gap: var(--spacing-2, 8px);
    align-self: stretch;
}
.show-more-text {
    cursor: pointer;
    color: #31708F;
    font-size: var(--spacing-3, 12px);
    font-style: normal;
    font-weight: 700;
    line-height: var(--spacing-6, 24px);
}

/* Style the dropdown chevron icon */
:deep(.multi-select-chevron),
:deep(.al-icon),
:deep([class*="chevron"]),
:deep([class*="icon-chevron"]) {
  font-family: playvox-icons;
  font-weight: 400;
  font-style: normal;
  font-size: 24px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Position the icon at the end with proper spacing */
:deep(.multi-select) {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.multi-select [slot="label"]),
:deep(.multi-select)::part(display-value),
:deep(.facet-label) {
  flex: 1;
  margin-right: 24px;
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Style dropdown options */
:deep(al-select-option),
:deep(.al-select-option),
:deep([role="option"]),
:deep(.option) {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}

/* Style option group labels */
:deep(al-select-option-group),
:deep(.al-select-option-group),
:deep([role="group"]) {
  font-family: Roboto;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #1A2021;
}
</style>